import { createContext, useContext } from "react";
import { TimelineBuilder, type TimelineOperation } from "./timeline.js";
import type {
  CompositionDefinition,
  DissolveProps,
  FadeThroughProps,
  SlideProps,
  TypeMatchCutProps,
  WhipPanCutProps,
  ZoomThroughTransitionProps,
  SequenceDefinition,
} from "./types.js";

const SAFE_ID = /^[A-Za-z][A-Za-z0-9_-]*$/;

export class CompileCollector {
  composition: CompositionDefinition | null = null;
  readonly operations: TimelineOperation[] = [];
  readonly initializers: string[] = [];
  readonly sequences = new Map<string, SequenceDefinition>();
  readonly componentIds = new Set<string>();
  readonly dissolves: DissolveProps[] = [];
  readonly slides: SlideProps[] = [];
  readonly fadeThroughs: FadeThroughProps[] = [];
  readonly typeMatchCuts: TypeMatchCutProps[] = [];
  readonly whipPanCuts: WhipPanCutProps[] = [];
  readonly zoomThroughTransitions: ZoomThroughTransitionProps[] = [];

  registerComposition(definition: CompositionDefinition): void {
    if (this.composition) throw new Error("Only one HFComposition root is allowed.");
    this.assertSafeId(definition.id, "composition");
    if (definition.duration <= 0) throw new Error("Composition duration must be positive.");
    if (definition.width <= 0 || definition.height <= 0) {
      throw new Error("Composition width and height must be positive.");
    }
    this.composition = definition;
  }

  registerSequence(definition: SequenceDefinition): void {
    this.assertUniqueId(definition.id, "sequence");
    if (definition.start < 0 || definition.duration <= 0 || definition.track < 0) {
      throw new Error(`Invalid timing for sequence "${definition.id}".`);
    }
    this.sequences.set(definition.id, definition);
  }

  registerComponent(id: string): void {
    this.assertUniqueId(id, "component");
    this.componentIds.add(id);
  }

  registerDissolve(definition: DissolveProps): void {
    if (definition.duration <= 0) throw new Error("Dissolve duration must be positive.");
    this.dissolves.push(definition);
  }

  registerSlide(definition: SlideProps): void {
    if (definition.duration <= 0) throw new Error("Slide duration must be positive.");
    this.slides.push(definition);
  }

  registerFadeThrough(definition: FadeThroughProps): void {
    this.assertUniqueId(definition.id, "fade-through");
    if (definition.duration <= 0) throw new Error("Fade-through duration must be positive.");
    if (definition.strength !== undefined && (definition.strength < 0.1 || definition.strength > 1)) {
      throw new Error("Fade-through strength must be between 0.1 and 1.");
    }
    this.componentIds.add(definition.id);
    this.fadeThroughs.push(definition);
  }

  registerTypeMatchCut(definition: TypeMatchCutProps): void {
    this.registerNamedTransition(definition, "type-match-cut");
    this.typeMatchCuts.push(definition);
  }

  registerWhipPanCut(definition: WhipPanCutProps): void {
    this.registerNamedTransition(definition, "whip-pan-cut");
    if (definition.lead !== undefined && definition.lead < 0) throw new Error("Whip-pan lead must not be negative.");
    this.whipPanCuts.push(definition);
  }

  registerZoomThroughTransition(definition: ZoomThroughTransitionProps): void {
    this.registerNamedTransition(definition, "zoom-through-transition");
    this.zoomThroughTransitions.push(definition);
  }

  registerInitializer(code: string): void {
    this.initializers.push(code);
  }

  timeline(offset = 0): TimelineBuilder {
    return new TimelineBuilder((operation) => this.operations.push(operation), offset);
  }

  validate(): void {
    if (!this.composition) throw new Error("An HFComposition root is required.");

    for (const sequence of this.sequences.values()) {
      if (sequence.start + sequence.duration > this.composition.duration) {
        throw new Error(`Sequence "${sequence.id}" extends beyond the composition duration.`);
      }
    }

    for (const dissolve of this.dissolves) {
      this.validateTransition(dissolve);
    }
    for (const slide of this.slides) {
      this.validateTransition(slide);
    }
    for (const fadeThrough of this.fadeThroughs) {
      this.validateTransition(fadeThrough);
    }
    for (const transition of this.typeMatchCuts) this.validateTransition(transition);
    for (const transition of this.whipPanCuts) this.validateTransition(transition);
    for (const transition of this.zoomThroughTransitions) this.validateTransition(transition);
  }

  private registerNamedTransition(definition: TypeMatchCutProps | WhipPanCutProps | ZoomThroughTransitionProps, kind: string): void {
    this.assertUniqueId(definition.id, kind);
    if (definition.duration <= 0) throw new Error(`${kind} duration must be positive.`);
    this.componentIds.add(definition.id);
  }

  private validateTransition(transition: DissolveProps): void {
      const from = this.sequences.get(transition.from);
      const to = this.sequences.get(transition.to);
      if (!from || !to) {
        throw new Error(`Transition references missing sequence "${!from ? transition.from : transition.to}".`);
      }
      const end = transition.at + transition.duration;
      if (transition.at < from.start || end > from.start + from.duration) {
        throw new Error(`Transition falls outside outgoing sequence "${from.id}".`);
      }
      if (transition.at < to.start || end > to.start + to.duration) {
        throw new Error(`Transition falls outside incoming sequence "${to.id}".`);
      }
  }

  private assertUniqueId(id: string, kind: string): void {
    this.assertSafeId(id, kind);
    if (this.sequences.has(id) || this.componentIds.has(id)) {
      throw new Error(`Duplicate stable id "${id}".`);
    }
  }

  private assertSafeId(id: string, kind: string): void {
    if (!SAFE_ID.test(id)) {
      throw new Error(`Invalid ${kind} id "${id}". Use letters, numbers, underscores, and hyphens.`);
    }
  }
}

export const CompileContext = createContext<CompileCollector | null>(null);
export const SequenceTimeContext = createContext(0);

export function useCollector(): CompileCollector {
  const collector = useContext(CompileContext);
  if (!collector) throw new Error("HyperFrames React components must be compiled with compileComposition().");
  return collector;
}
